import * as React from 'react';

import Checkbox from 'af-components/Controls/Checkbox';
import Tag from 'af-components/Controls/Tag';

import styles from './styles.module.scss';

type WithDropdownOptions = {
	withDropdownOptions: true;
	options: string[];
	hasFreeInputOption?: boolean;
	initiallyOpened?: boolean;
};

type WithoutDropdownOptions = {
	withDropdownOptions?: false;
	options?: never;
	hasFreeInputOption?: never;
	initiallyOpened?: never;
};

export type DropdownProps = WithoutDropdownOptions | WithDropdownOptions;

type Props = {
	secondaryOptions: Nullable<string>[];
	setSecondaryOption: (value: Nullable<string>[]) => void;
	hasEmptyOption?: boolean;
	placeholder?: string;
} & DropdownProps;

const TextSecondaryMultiple: React.FC<Props> = (props) => {
	const { secondaryOptions, setSecondaryOption, withDropdownOptions, hasFreeInputOption, options, hasEmptyOption, placeholder, initiallyOpened } = props;
	const inputWrapperRef = React.useRef<HTMLDivElement>(null);

	const [currentTextValue, setCurrentTextValue] = React.useState<string>('');
	const [showDropdown, setShowDropdown] = React.useState<boolean>(withDropdownOptions ? !!initiallyOpened : false);

	const secondaryOptionsSet = React.useMemo(() => {
		return new Set(secondaryOptions);
	}, [secondaryOptions]);

	const onValueChange = React.useCallback((value: React.ChangeEvent<HTMLInputElement>) => {
		setCurrentTextValue(value.target.value);
	}, []);

	const handleKeyDown = React.useCallback((e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && currentTextValue.trim()) {
			setSecondaryOption([...secondaryOptions, currentTextValue]);
			setCurrentTextValue('');
			setShowDropdown(false);
		}
	}, [currentTextValue, secondaryOptions, setSecondaryOption]);

	const onAddFreeInputClick = React.useCallback(() => {
		if (currentTextValue.trim()) {
			setSecondaryOption([...secondaryOptions, currentTextValue.trim()]);
			setCurrentTextValue('');
			setShowDropdown(false);
		}
	}, [currentTextValue, secondaryOptions, setSecondaryOption]);

	const onBlur = React.useCallback((e) => {
		if (
			inputWrapperRef.current &&
			e.relatedTarget &&
			inputWrapperRef.current.contains(e.relatedTarget as Node)
		) {
			// focus inside dropdown
			return;
		}
		setShowDropdown(false);
	}, []);

	const onRemove = React.useCallback((_index: number) => () => {
		const newOptions = secondaryOptions.filter((_, i) => i !== _index);
		setSecondaryOption(newOptions);
	}, [secondaryOptions, setSecondaryOption]);

	const onClick = React.useCallback(() => {
		setShowDropdown(!showDropdown);
	}, [showDropdown]);

	const dropdownOptions = React.useMemo(() => {
		let _dropdownOptions: Nullable<string>[] = [];
		if (withDropdownOptions && options) {
			_dropdownOptions = [..._dropdownOptions, ...options];
		}
		if (hasEmptyOption) {
			_dropdownOptions.push(null);
		}
		return _dropdownOptions;
	}, [hasEmptyOption, options, withDropdownOptions]);

	const onDropdownItemClick = React.useCallback((dropdownItem: Nullable<string>) => (e) => {
		e.preventDefault();
		if (secondaryOptionsSet.has(dropdownItem)) {
			setSecondaryOption(secondaryOptions.filter((_option) => dropdownItem !== _option));
		} else {
			setSecondaryOption([...secondaryOptions, dropdownItem]);
		}
	}, [secondaryOptions, secondaryOptionsSet, setSecondaryOption]);

	const dropdownOptionsMapper = React.useCallback((_option) => {
		const isChecked = secondaryOptionsSet.has(_option);

		return (
			<div className={styles['text-secondary__dropdown__item']} key={_option} onClick={onDropdownItemClick(_option)} >
				<span className={styles['text-secondary__dropdown__item__label']}>
					{_option !== null
						? _option
						: <span className={styles['text-secondary__dropdown__item__label--empty']}>Empty (no content)</span>
					}
				</span>
				<Checkbox handleChange={onDropdownItemClick(_option)} isChecked={isChecked} />
			</div>
		);
	}, [onDropdownItemClick, secondaryOptionsSet]);

	const resolveDropdown = React.useCallback(() => {
		return (
			<div className={styles['text-secondary__dropdown']}>
				{dropdownOptions.map(dropdownOptionsMapper)}
				{!!currentTextValue.trim() && (
					<div className={styles['text-secondary__dropdown__item']} key={currentTextValue}>
						<span className={styles['text-secondary__dropdown__item__label']} onClick={onAddFreeInputClick}>
							{`Search by: ${currentTextValue}`}
						</span>
					</div>
				)}
			</div>
		);
	}, [currentTextValue, dropdownOptions, dropdownOptionsMapper, onAddFreeInputClick]);

	const isSearchReadOnly = withDropdownOptions && !hasFreeInputOption;

	const tagMapper = React.useCallback((_option: Nullable<string>, _index: number) => {
		const isEmptyOption = _option === null;
		const emptyClassname = isEmptyOption ? `${styles['text-secondary__single-tag__option--empty']}` : '';

		return (
			<Tag contentClassName={styles['text-secondary__single-tag']} key={_option} >
				<span className={`${styles['text-secondary__single-tag__option']} ${emptyClassname}`}>
					{!isEmptyOption
						? _option
						: 'Empty'
					}
				</span>
				<span className="icon-close" onClick={onRemove(_index)} role="button" />
			</Tag>
		);
	}, [onRemove]);

	return (
		<div className={styles['text-secondary']}>
			<div className={styles['text-secondary__search-input-wrapper']} onBlur={onBlur} ref={inputWrapperRef} tabIndex={-1}>
				<input
					className="form-control"
					onChange={onValueChange}
					onClick={onClick}
					onKeyDown={handleKeyDown}
					placeholder={placeholder ?? 'Enter Text'}
					readOnly={isSearchReadOnly}
					type="text"
					value={currentTextValue}
				/>
				<span className="icon-down" onClick={onClick} />
				{showDropdown && (
					resolveDropdown()
				)}
			</div>
			<div className={styles['text-secondary__tags']}>
				{secondaryOptions.map(tagMapper)}
			</div>
		</div>
	);
};

export default React.memo(TextSecondaryMultiple);
